import type { Experiment } from '../api.js';
import { formatDate, formatDateTime } from '../lib/format.js';

/**
 * §102.3 — DENEY TARİH SÖZLEŞMESİ.
 *
 * Deneyin tarihleri `018_experiments.sql`de baştan beri duruyordu ve panel
 * HİÇBİRİNİ çizmiyordu: "ne zaman başladı, kaç gündür sürüyor, ne zaman biter"
 * sorularının cevabı üründe yoktu, Postgres'teydi.
 *
 * BU BİLEŞENİN TEK KURALI: gerçek olan yazılır, olmayan SÖYLENİR.
 *   · `startedAt` yoksa süre YAZILMAZ (taslak deneyin süresi yoktur).
 *   · `autoConcludeAt` NULL ise tarih UYDURULMAZ. Panel `startedAt + 30` hesabı
 *     YAPAMAZ: sınır worker'ın `EXP_MAX_DAYS`indedir ve kod varsayılanı (30) ile
 *     referans üretim örneği (60) farklıdır — hesaplasaydık, o kurulumda ekranda
 *     yanlış bir tarih dururdu (§101.2 KARAR 1). Tarihi, sınırı UYGULAYAN süreç
 *     damgalar; damga gelene kadar panel bunu açıkça söyler.
 *
 * `now` dışarıdan verilebilir (`formatAgo` deseni): süre metinleri makine saatine
 * bağlı olmadan test edilebilsin.
 */

const GUN_MS = 24 * 60 * 60 * 1000;

/** Tam gün farkı; negatifse 0. */
function gunFarki(fromMs: number, toMs: number): number {
  return Math.max(0, Math.floor((toMs - fromMs) / GUN_MS));
}

/**
 * Deneyin süre cümlesi — `null` ise süre gösterilecek bir şey yok (taslak).
 * Dışa açıktır: hem kart hem detay aynı cümleyi kurar.
 */
export function sureMetni(experiment: Experiment, now: number): string | null {
  if (experiment.startedAt === null) return null;
  const basladi = Date.parse(experiment.startedAt);
  if (Number.isNaN(basladi)) return null;

  if (experiment.status === 'running') {
    const gun = gunFarki(basladi, now);
    return gun === 0 ? 'bugün başladı' : `${gun} gündür sürüyor`;
  }

  if (experiment.concludedAt === null) return null;
  const bitti = Date.parse(experiment.concludedAt);
  if (Number.isNaN(bitti)) return null;
  const gun = gunFarki(basladi, bitti);
  return gun === 0 ? 'aynı gün içinde bitti' : `${gun} gün sürdü`;
}

/** Kapanışa kalan süre — `autoConcludeAt` yoksa çağrılmaz. */
function kalanMetni(autoConcludeAt: string, now: number): string {
  const kapanis = Date.parse(autoConcludeAt);
  if (Number.isNaN(kapanis)) return '';
  if (kapanis <= now) return 'süre doldu — bir sonraki değerlendirmede kapanır';
  const gun = Math.ceil((kapanis - now) / GUN_MS);
  return `${gun} gün kaldı`;
}

interface Satir {
  etiket: string;
  deger: string;
  /** İkincil açıklama (süre, kalan gün) — değerin altına küçük punto yazılır. */
  not?: string;
  testId: string;
}

/** §102.3 tablosu — durum ne olursa olsun aynı sırayla, yalnız geçerli satırlar. */
function satirlar(experiment: Experiment, now: number): Satir[] {
  const out: Satir[] = [
    { etiket: 'Oluşturuldu', deger: formatDate(experiment.createdAt), testId: 'deney-tarih-olusturuldu' },
  ];

  if (experiment.startedAt === null) {
    out.push({ etiket: 'Başladı', deger: 'Henüz başlatılmadı', testId: 'deney-tarih-basladi' });
  } else {
    const sure = sureMetni(experiment, now);
    out.push({
      etiket: 'Başladı',
      deger: formatDateTime(experiment.startedAt),
      ...(sure === null ? {} : { not: sure }),
      testId: 'deney-tarih-basladi',
    });
  }

  if (experiment.concludedAt !== null) {
    out.push({
      etiket: experiment.status === 'stopped' ? 'Durduruldu' : 'Sonuçlandı',
      deger: formatDateTime(experiment.concludedAt),
      testId: 'deney-tarih-bitti',
    });
  }

  if (experiment.status === 'running') {
    out.push(
      experiment.autoConcludeAt === null
        ? {
            etiket: 'Otomatik kapanış',
            deger: 'İlk değerlendirmeden sonra belli olur',
            testId: 'deney-tarih-kapanis',
          }
        : {
            etiket: 'Otomatik kapanış',
            deger: formatDate(experiment.autoConcludeAt),
            not: kalanMetni(experiment.autoConcludeAt, now),
            testId: 'deney-tarih-kapanis',
          },
    );
  }

  return out;
}

export default function DeneyTarihleri({
  experiment,
  compact = false,
  now = Date.now(),
}: {
  experiment: Experiment;
  /** Liste kartında tek satır, detay sayfasında ızgara. */
  compact?: boolean;
  now?: number;
}) {
  const rows = satirlar(experiment, now);

  if (compact) {
    return (
      <p className="mt-1 text-[12px] text-[#9ca3af]" data-testid="deney-tarihleri">
        {rows.map((r, i) => (
          <span key={r.etiket} data-testid={r.testId}>
            {i > 0 ? ' · ' : ''}
            {r.etiket}: {r.deger}
            {r.not === undefined || r.not === '' ? '' : ` (${r.not})`}
          </span>
        ))}
      </p>
    );
  }

  return (
    <dl
      className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4"
      data-testid="deney-tarihleri"
    >
      {rows.map((r) => (
        <div key={r.etiket} data-testid={r.testId}>
          <dt className="text-[12px] text-[#9ca3af]">{r.etiket}</dt>
          <dd className="text-[13px] font-medium text-[#111827]">{r.deger}</dd>
          {r.not === undefined || r.not === '' ? null : (
            <dd className="text-[12px] text-[#6b7280]">{r.not}</dd>
          )}
        </div>
      ))}
    </dl>
  );
}
